import React from "react";
import PropTypes from "prop-types";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

import { useUpdateRoleAssignmentsMutation } from "state/rtkQuery/access/accessMutations";
import { buildSetActivePayload } from "utils/accessManagement/roleAssignmentForm";
import { getAssigneeDisplay } from "utils/accessManagement/roleAssignmentDisplay";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";
import { showToast } from "utils/reusableToast";

// Confirms BOTH deactivating and reactivating an assignment — previously
// reactivate skipped confirmation and fired immediately, deciding which
// action to take from the row object captured in the grid's onClick closure
// at the moment it was clicked. If that row was ever stale (e.g. a DataGrid
// row-data race right after switching to a different user's modal, or after
// another assignment's mutation had just invalidated the shared query cache)
// the wrong action fired with no chance to catch it: a real report was
// deactivating one person, then immediately reactivating a different one
// with no confirmation shown at all. Always asking for confirmation here,
// and always deciding the actual is_active target from THIS dialog's own
// `assignment` prop at confirm time (not a value decided earlier by the
// caller), means a stale click can only ever open the wrong confirmation —
// still visibly wrong and cancelable — never silently take the wrong action.
const DeactivateConfirmDialog = ({ assignment, onClose }) => {
  const [updateAssignments, { isLoading }] = useUpdateRoleAssignmentsMutation();
  const { platformId } = useAccessPlatformId();

  if (!assignment) return null;

  const { primary } = getAssigneeDisplay(assignment);
  const isDeactivating = assignment.is_active;

  const handleConfirm = async () => {
    try {
      const result = await updateAssignments(buildSetActivePayload([assignment.id], !isDeactivating, assignment.platform_id || platformId)).unwrap();
      if (result.not_updated?.length) {
        showToast({
          title: isDeactivating ? "Deactivation failed" : "Reactivation failed",
          description: result.not_updated[0]?.message || `The assignment could not be ${isDeactivating ? "deactivated" : "reactivated"}.`,
          type: "error",
        });
      } else {
        showToast({
          title: isDeactivating ? "Assignment deactivated" : "Assignment reactivated",
          description: isDeactivating ? `${primary} will no longer receive access from this assignment.` : undefined,
          type: "success",
        });
      }
      onClose();
    } catch {
      showToast({
        title: isDeactivating ? "Deactivation failed" : "Reactivation failed",
        description: "Please try again.",
        type: "error",
      });
    }
  };

  return (
    <Dialog
      open={!!assignment}
      onClose={onClose}
      // Opened from a button inside the group-assignments Dialog — without an
      // explicit z-index above the shared default modal layer, this can
      // render behind that already-open Dialog instead of on top of it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <DialogTitle>{isDeactivating ? "Deactivate assignment?" : "Reactivate assignment?"}</DialogTitle>
      <DialogContent sx={{ maxWidth: 420 }}>
        <DialogContentText>
          {isDeactivating ? (
            <>
              <strong>{primary}</strong> will no longer receive permissions from this role assignment. This does not
              delete the assignment record — you can reactivate it later.
            </>
          ) : (
            <>
              <strong>{primary}</strong> will start receiving permissions from this role assignment again.
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={(event) => {
            // This dialog is stacked on top of the group-assignments Dialog
            // (see the zIndex note above). Without stopping propagation here,
            // React's delegated event system can let this same click bubble
            // out to the OUTER Dialog's backdrop-click-away detection after
            // this dialog unmounts mid-click — closing the group modal
            // behind it too, not just this confirmation.
            event.stopPropagation();
            handleConfirm();
          }}
          disabled={isLoading}
          color={isDeactivating ? "warning" : "primary"}
          variant="contained"
        >
          {isDeactivating ? "Deactivate" : "Reactivate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeactivateConfirmDialog.propTypes = {
  assignment: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default DeactivateConfirmDialog;
